import React, { useState, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import PermissionGate from '../../shared/PermissionGate';
import { PERMISSIONS } from '../../../constants/permissionMappings';

/**
 * QuickAddPopover - Inline event creation at click position
 *
 * Features:
 * - Appears at click position on calendar
 * - Minimal fields for quick creation (title, time, category)
 * - "More Details" button expands to full slideout
 * - Click outside to close
 * - Escape key to close
 */
const QuickAddPopover = ({
  isOpen,
  position,
  selectedDate,
  selectedTime,
  onClose,
  onSubmit,
  onExpand
}) => {
  const [title, setTitle] = useState('');
  const [eventType, setEventType] = useState('appointment');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [allDay, setAllDay] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const popoverRef = useRef(null);
  const titleInputRef = useRef(null);

  // Reset form when popover opens
  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setEventType('appointment');
      setError(null);
      setIsSubmitting(false);

      // Set default times based on selected time or current time
      if (selectedTime) {
        setStartTime(selectedTime);
        // Parse time and add 1 hour for end time
        const [hours, minutes] = selectedTime.split(':').map(Number);
        const endHours = Math.min(hours + 1, 23);
        setEndTime(`${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`);
      } else {
        const now = new Date();
        const nextHour = Math.ceil(now.getHours());
        setStartTime(`${nextHour.toString().padStart(2, '0')}:00`);
        setEndTime(`${Math.min(nextHour + 1, 23).toString().padStart(2, '0')}:00`);
      }

      // Focus title input after render
      setTimeout(() => {
        titleInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, selectedTime]);

  // Handle escape key to close
  // Note: Click-outside is handled by CalendarView's handleDateClick to prevent
  // race conditions between mousedown (click-outside) and onClick (day cell)
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Calculate popover position (ensure it stays within viewport)
  const getPopoverStyle = () => {
    if (!position) return {};

    const isMobile = window.innerWidth < 640;
    const popoverWidth = isMobile ? Math.min(300, window.innerWidth - 32) : 320;
    const popoverHeight = 350;
    const padding = 16;

    let left = position.x - popoverWidth / 2;
    let top = position.y + 10;

    // Adjust horizontal position
    if (left < padding) {
      left = padding;
    } else if (left + popoverWidth > window.innerWidth - padding) {
      left = window.innerWidth - popoverWidth - padding;
    }

    // Adjust vertical position (show above if near bottom)
    if (top + popoverHeight > window.innerHeight - padding) {
      top = position.y - popoverHeight - 10;
    }

    // On mobile, ensure minimum top position
    if (isMobile && top < padding) {
      top = padding;
    }

    return {
      position: 'fixed',
      left: `${left}px`,
      top: `${top}px`,
      width: isMobile ? `${popoverWidth}px` : undefined,
      zIndex: 50
    };
  };

  // Derive category from event type
  const getCategoryFromEventType = (type) => {
    switch (type) {
      case 'shift':
        return 'schedule';
      case 'appointment':
        return 'external';
      case 'meeting':
      case 'task':
        return 'internal';
      case 'reminder':
      case 'time_block':
        return 'personal';
      default:
        return 'external';
    }
  };

  // Build event data
  const buildEventData = () => {
    if (!selectedDate) return null;

    let eventStartTime, eventEndTime;

    if (allDay) {
      eventStartTime = new Date(selectedDate);
      eventStartTime.setHours(0, 0, 0, 0);
      eventEndTime = new Date(selectedDate);
      eventEndTime.setHours(23, 59, 59, 999);
    } else {
      const [startHours, startMinutes] = startTime.split(':').map(Number);
      const [endHours, endMinutes] = endTime.split(':').map(Number);

      eventStartTime = new Date(selectedDate);
      eventStartTime.setHours(startHours, startMinutes, 0, 0);

      eventEndTime = new Date(selectedDate);
      eventEndTime.setHours(endHours, endMinutes, 0, 0);
    }

    return {
      title: title.trim(),
      startTime: eventStartTime.toISOString(),
      endTime: eventEndTime.toISOString(),
      category: getCategoryFromEventType(eventType),
      allDay,
      eventType
    };
  };

  // Handle quick submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      setError('Please enter a title');
      return;
    }

    const eventData = buildEventData();
    if (!eventData) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(eventData);
    } catch (err) {
      setError(err.message || 'Failed to create event');
      setIsSubmitting(false);
    }
  };

  // Handle expand to full editor
  const handleExpand = () => {
    const eventData = buildEventData();
    onExpand(eventData || { title, eventType, category: getCategoryFromEventType(eventType) });
  };

  if (!isOpen) return null;

  const eventTypes = [
    { key: 'shift', label: 'Shift', color: 'bg-orange-500', category: 'schedule' },
    { key: 'appointment', label: 'Appointment', color: 'bg-blue-500', category: 'external' },
    { key: 'meeting', label: 'Meeting', color: 'bg-purple-500', category: 'internal' },
    { key: 'task', label: 'Task', color: 'bg-amber-500', category: 'internal' },
    { key: 'reminder', label: 'Reminder', color: 'bg-green-500', category: 'personal' },
    { key: 'time_block', label: 'Time Block', color: 'bg-gray-500', category: 'personal' }
  ];

  return (
    <div
      ref={popoverRef}
      style={getPopoverStyle()}
      className="w-full sm:w-80 max-w-[calc(100vw-32px)] bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden"
    >
      <form onSubmit={handleSubmit}>
        {/* Header */}
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-900">
            New Event
            {selectedDate && (
              <span className="text-gray-500 font-normal ml-2">
                {format(selectedDate, 'MMM d, yyyy')}
              </span>
            )}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {/* Error display */}
          {error && (
            <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
              {error}
            </div>
          )}

          {/* Title input */}
          <div>
            <input
              ref={titleInputRef}
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Add title"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
            />
          </div>

          {/* Event type selector */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Event Type</label>
            <select
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent bg-white"
            >
              {eventTypes.map((type) => (
                <option key={type.key} value={type.key}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* All day toggle */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="allDay"
              checked={allDay}
              onChange={(e) => setAllDay(e.target.checked)}
              className="rounded border-gray-300 text-brand focus:ring-brand"
            />
            <label htmlFor="allDay" className="text-sm text-gray-700">
              All day
            </label>
          </div>

          {/* Time inputs (hidden for all-day events) */}
          {!allDay && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Start</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">End</label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <button
            type="button"
            onClick={handleExpand}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            More details
          </button>
          <PermissionGate permission={PERMISSIONS.scheduling.create}>
            <button
              type="submit"
              disabled={isSubmitting || !title.trim()}
              className="px-4 py-1.5 text-sm font-medium text-white bg-brand rounded-md hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
          </PermissionGate>
        </div>
      </form>
    </div>
  );
};

export default QuickAddPopover;
